import { useCallback } from "react";
import {
  Measurement,
  makeMeasurementsFetchPage,
  type MeasurementsListResponse,
  type NewMeasurement,
  type PatchMeasurement,
} from "@heraion/shared";
import { useAsyncList } from "@heraion/ui";
import { apiFetch } from "./api";

// The web console's measurements resource (/api/me/measurements). Online
// only — no offline outbox (that stays on mobile); a failed write returns
// false and the shared Form keeps the typed values + surfaces a notice.
// Same endpoints + payloads as the mobile useMeasurements, so a write
// round-trips the exact schema the API already accepts.
//
// PG-37 — reads now go through `useAsyncList` (@heraion/ui), the SAME
// primitive mobile uses. Until 2026-09-18 this hook walked the cursor in a
// hand-rolled `do { fetch } while (cursor)` that drained EVERY page on
// mount, while mobile stopped at the server's first page with no way to ask
// for more: one screen, two behaviours, and neither was the intended one.
// Swapping the loop for shared `paginate()` would have shortened the code
// and kept the divergence — so the loop is gone, not tidied. This is also
// what CN-47 lifted useAsyncList out of apps/mobile FOR: web-rnw could not
// import it before, and every surface here re-invented the walk instead.
//
// ⛔ No `cache` is injected: the console has no offline story, and a cache
// the athlete cannot invalidate is worse here than a cold fetch.

const fetchPage = makeMeasurementsFetchPage((path) =>
  apiFetch<MeasurementsListResponse>(path),
);

const newId = (): string =>
  typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
    ? crypto.randomUUID()
    : `${Date.now().toString(36)}-${Math.round(Math.random() * 1e9).toString(36)}`;

export type UseMeasurementsWeb = {
  items: Measurement[];
  loading: boolean;
  loadFailed: boolean;
  /** More rows exist on the server than are on screen (PG-37). */
  hasMore: boolean;
  /** Fetch the next page and append it. No-op when `hasMore` is false. */
  loadMore: () => Promise<void>;
  /** A next page is in flight (the control says so instead of going dead). */
  loadingMore: boolean;
  create: (payload: NewMeasurement) => Promise<boolean>;
  update: (id: string, patch: PatchMeasurement) => Promise<boolean>;
  remove: (id: string) => Promise<boolean>;
};

export function useMeasurementsWeb(): UseMeasurementsWeb {
  const list = useAsyncList<Measurement, string>({
    key: "measurements",
    fetchPage,
  });
  const { refresh } = list;
  const items = list.items ?? [];

  const create = useCallback(
    async (payload: NewMeasurement) => {
      const body: NewMeasurement = { ...payload, id: payload.id ?? newId() };
      try {
        await apiFetch<Measurement>("/api/me/measurements", { method: "POST", body });
        await refresh();
        return true;
      } catch {
        return false;
      }
    },
    [refresh],
  );

  const update = useCallback(
    async (id: string, patch: PatchMeasurement) => {
      try {
        await apiFetch<Measurement>(`/api/me/measurements/${id}`, {
          method: "PATCH",
          body: patch,
        });
        await refresh();
        return true;
      } catch {
        return false;
      }
    },
    [refresh],
  );

  const remove = useCallback(
    async (id: string) => {
      try {
        await apiFetch<unknown>(`/api/me/measurements/${id}`, { method: "DELETE" });
        await refresh();
        return true;
      } catch {
        return false;
      }
    },
    [refresh],
  );

  return {
    items,
    // A refresh that fails with rows already on screen keeps them visible;
    // only an empty-and-failed load is a banner (same reading as mobile).
    loading: list.status === "loading" && items.length === 0,
    loadFailed: list.status === "error" && items.length === 0,
    hasMore: list.hasMore,
    loadMore: list.loadMore,
    loadingMore: list.status === "loading_more",
    create,
    update,
    remove,
  };
}
